import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Package, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  { key: 'asset_type', label: 'Asset Type', type: 'select', options: ['vehicle','truck','equipment','machinery','technology','solar','agricultural','medical','other'] },
  { key: 'description', label: 'Description', type: 'text', wide: true, required: true },
  { key: 'supplier_name', label: 'Supplier', type: 'text', required: true },
  { key: 'new_or_used', label: 'New / Used', type: 'select', options: ['new', 'used', 'demo'] },
  { key: 'purchase_price', label: 'Purchase Price ($)', type: 'number', required: true },
  { key: 'deposit_amount', label: 'Deposit ($)', type: 'number' },
  { key: 'balloon_percent', label: 'Balloon / Residual (%)', type: 'number' },
  { key: 'loan_term_months', label: 'Loan Term (months)', type: 'number' },
  { key: 'gst_inclusive', label: 'GST Inclusive', type: 'select', options: ['true', 'false'] },
  { key: 'invoice_number', label: 'Invoice #', type: 'text' },
  { key: 'vin_or_serial', label: 'VIN / Serial', type: 'text' },
];

export default function AssetsTab({ deal, dealId, onRefresh }) {
  const assets = deal.assets || [];
  const [expanded, setExpanded] = useState(assets[0]?.id || null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(null);

  const handleAdd = async () => {
    try {
      const prefill = !assets.length ? { description: deal.asset_description, new_or_used: deal.is_new_or_used, purchase_price: deal.amount } : {};
      const res = await axios.post(`${API}/crm/deals/${dealId}/assets`, { deal_id: dealId, ...prefill });
      toast.success('Asset added');
      onRefresh();
      setExpanded(res.data.id);
    } catch (e) { toast.error('Failed to add asset'); }
  };

  const handleSave = async (id) => {
    setSaving(id);
    try {
      const data = { ...(editing[id] || {}) };
      if (data.purchase_price !== undefined) data.purchase_price = Number(data.purchase_price) || 0;
      if (data.deposit_amount !== undefined) data.deposit_amount = data.deposit_amount === '' ? null : Number(data.deposit_amount);
      if (data.balloon_percent !== undefined) data.balloon_percent = data.balloon_percent === '' ? null : Number(data.balloon_percent);
      if (data.loan_term_months !== undefined) data.loan_term_months = data.loan_term_months === '' ? null : Number(data.loan_term_months);
      if (data.year !== undefined) data.year = Number(data.year) || null;
      if (data.gst_inclusive !== undefined) data.gst_inclusive = data.gst_inclusive === 'true';
      if (data.insurance_required !== undefined) data.insurance_required = data.insurance_required === 'true';
      await axios.put(`${API}/crm/assets/${id}`, data);
      toast.success('Asset saved');
      onRefresh();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try { await axios.delete(`${API}/crm/assets/${id}`); toast.success('Deleted'); onRefresh(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const updateField = (id, key, value) => {
    setEditing(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  const handleInvoiceUpload = async (assetId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    setUploadingInvoice(assetId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('deal_id', dealId);
      fd.append('category', 'asset_quote');
      const uploadRes = await axios.post(`${API}/crm/documents/upload`, fd);
      await axios.put(`${API}/crm/assets/${assetId}`, { invoice_document_id: uploadRes.data.id });
      toast.success('Invoice uploaded and linked');
      onRefresh();
    } catch {
      toast.error('Failed to upload invoice');
    } finally {
      setUploadingInvoice(null);
    }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="assets-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2" data-testid="asset-deal-security-title"><Package className="h-5 w-5 text-orange-400" /> Asset (Deal Security)</h2>
        <Button onClick={handleAdd} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-asset-btn">
          <Plus className="h-4 w-4 mr-1" />Add Asset
        </Button>
      </div>
      {assets.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">No assets yet. Click Add Asset to create one.</div>
      )}
      {assets.map(asset => {
        const isOpen = expanded === asset.id;
        const formData = { ...asset, ...(editing[asset.id] || {}), gst_inclusive: String(asset.gst_inclusive ?? ''), insurance_required: String(asset.insurance_required ?? 'true') };
        if (editing[asset.id]?.gst_inclusive !== undefined) formData.gst_inclusive = editing[asset.id].gst_inclusive;
        if (editing[asset.id]?.insurance_required !== undefined) formData.insurance_required = editing[asset.id].insurance_required;
        return (
          <div key={asset.id} className="bg-navy-800/50 rounded-xl border border-white/5 overflow-hidden" data-testid={`asset-card-${asset.id}`}>
            <button onClick={() => setExpanded(isOpen ? null : asset.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium text-sm">{asset.description || asset.make || 'New Asset'}</span>
                <span className="text-xs text-gray-500">${(asset.purchase_price || 0).toLocaleString()}</span>
                <span className="text-xs text-gray-500 capitalize">{asset.new_or_used || ''}</span>
              </div>
              {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>
            {isOpen && (
              <div className="px-4 pb-4 border-t border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {FIELDS.map(f => (
                    <div key={f.key} className={f.wide ? 'md:col-span-2' : ''}>
                      <label className="block text-xs text-gray-400 mb-1">{f.label}{f.required && <span className="text-red-400">*</span>}</label>
                      {f.type === 'select' ? (
                        <select value={formData[f.key] ?? ''} onChange={e => updateField(asset.id, f.key, e.target.value)}
                          className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
                          <option value="">Select...</option>
                          {f.options.map(o => <option key={o} value={o}>{o === 'true' ? 'Yes' : o === 'false' ? 'No' : o}</option>)}
                        </select>
                      ) : (
                        <input type={f.type} value={formData[f.key] ?? ''} placeholder={f.placeholder || ''}
                          onChange={e => updateField(asset.id, f.key, e.target.value)}
                          className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-4">
                  <Button onClick={() => handleDelete(asset.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4 mr-1" />Delete
                  </Button>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center text-xs px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 cursor-pointer" data-testid={`upload-invoice-button-${asset.id}`}>
                      <Upload className="h-3.5 w-3.5 mr-1" /> {uploadingInvoice === asset.id ? 'Uploading...' : 'Upload Invoice'}
                      <input
                        type="file"
                        className="hidden"
                        onChange={e => e.target.files?.[0] && handleInvoiceUpload(asset.id, e.target.files[0])}
                      />
                    </label>
                    <Button onClick={() => handleSave(asset.id)} disabled={saving === asset.id} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`save-asset-${asset.id}`}>
                      <Save className="h-4 w-4 mr-1" />{saving === asset.id ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
